import { differenceInSeconds, format, isDate, isValid, parse } from "date-fns";
import { de, enUS } from "date-fns/locale";

export type DateFormat =
  | "daytime"
  | "day"
  | "time"
  | "apiDate"
  | "apiTime"
  | "apiDateTime"
  | "dayNoYear"
  | "dateWithDay";

export const dateTimeFormat = "dd.MM.yyyy HH:mm";

export const timeFormat = "HH:mm";

export const dateFormat = "dd.MM.yyyy";

export const apiDateFormat = "yyyy-MM-dd";

export const apiTime = "HH:mm:ss";

export const apiDateTime = "yyyy-MM-dd HH:mm:ss";

export const dayNoYearFormat = "dd.MM.";

export const dateWithDayFormat = "EE, dd.MM.yyyy";

export const getDateTimeFormat = (dateString: DateFormat) => {
  switch (dateString) {
    case "daytime":
      return dateTimeFormat;

    case "day":
      return dateFormat;

    case "time":
      return timeFormat;

    case "apiDate":
      return apiDateFormat;

    case "apiTime":
      return apiTime;

    case "apiDateTime":
      return apiDateTime;

    case "dateWithDay":
      return dateWithDayFormat;

    case "dayNoYear":
      return dayNoYearFormat;
  }
};

export const formatDate = (date: string | number | Date, dateString: DateFormat, locale?: string) => {
  if (!date || date === "N/A") return "-";

  const actualDate = isDate(date) ? (date as Date) : new Date(date);

  if (!isValid(actualDate)) return "-";

  return format(actualDate, getDateTimeFormat(dateString), {
    locale: locale === "de" ? de : enUS,
  });
};

export const parseDate = (date: string, dateString: DateFormat) => {
  return parse(date, getDateTimeFormat(dateString), new Date());
};

export const formatSecondsDuration = ({
  seconds,
  capAtHour,
  withSeconds,
  withDays,
}: {
  seconds: number;
  withSeconds?: boolean;
  capAtHour?: boolean;
  withDays?: boolean;
}) => {
  if (seconds <= 60) {
    return `${seconds}s`;
  }

  const minuteDiff = Math.floor(seconds / 60);

  if (minuteDiff <= 60) {
    return `${minuteDiff}m ${withSeconds && seconds % 60 !== 0 ? `${seconds % 60}s` : ""}`;
  }

  if (capAtHour) return "> 1h";

  const hourDiff = Math.floor(minuteDiff / 60);

  if (hourDiff <= 24 || !withDays) {
    return `${hourDiff}h ${minuteDiff % 60 !== 0 ? `${minuteDiff % 60}m` : ""} ${
      withSeconds && seconds % 60 !== 0 ? `${seconds % 60}s` : ""
    }`;
  }

  const dayDiff = Math.floor(hourDiff / 24);

  return `${dayDiff}d ${hourDiff % 24 !== 0 ? `${hourDiff % 24}h` : ""}`;
};

export function formatSecondsToMinutesAndSeconds(totalSeconds: number): string {
  if (!totalSeconds) return "00:00";
  // Calculate minutes and remaining seconds
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);

  // Format minutes and seconds to always have two digits
  const formattedMinutes = minutes.toString().padStart(2, "0");
  const formattedSeconds = seconds.toString().padStart(2, "0");

  // Return the formatted time
  return `${formattedMinutes}:${formattedSeconds}`;
}

export const formatDateRelative = ({
  date,
  dateLeft,
  withDays = false,
  withSeconds,
  capAtHour,
  alwaysShowMinutes,
}: {
  date: Date;
  dateLeft?: Date;
  withDays?: boolean;
  withSeconds?: boolean;
  capAtHour?: boolean;
  alwaysShowMinutes?: boolean;
}) => {
  if (!date || dateLeft === null) return "-";

  const secondDate = dateLeft ?? new Date();

  const secondsDiff = differenceInSeconds(secondDate, date);

  if (alwaysShowMinutes) {
    return formatSecondsToMinutesAndSeconds(secondsDiff);
  }

  return formatSecondsDuration({
    seconds: secondsDiff,
    withDays,
    withSeconds,
    capAtHour,
  });
};

export function fromUnix(unix: any) {
  return new Date(Number(unix) * 1000);
}

export function toUnix(date: Date | number) {
  if (typeof date === "number") return date;
  return Math.floor(date.getTime() / 1000);
}
